import React, { useEffect, useState, useCallback } from "react";
import Swal from 'sweetalert2';
import axios from 'axios';
import config from '../config';
import { formatChemicalFormula, formatNumber } from "../js/global";
import { useParams } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import AppShell from "../components/AppShell";
import WarningIcon from '@mui/icons-material/Warning';

function WaterTreatmentPlants() {
    const { id, years, fac_id, campus_id } = useParams();
    const [activities, setActivities] = useState([]);
    const [isDisabled, setIsDisabled] = useState(true);
    const [quantities, setQuantities] = useState([]);

    const fetchSystemOpen = useCallback(async () => {
        try {
            const yearAD = Number(years) - 543;
            const res = await axios.get(config.urlApi + `/activity/showPeriod/${fac_id}`);
            if (res.status === 200 && res.data.length > 0) {
                const currentPeriod = res.data.find((item) => Number(item.years) === yearAD);
                const isOpenRaw = currentPeriod?.is_open;
                const isOpen = isOpenRaw === true || isOpenRaw === 1 || isOpenRaw === '1';
                setIsDisabled(!isOpen);
            } else {
                setIsDisabled(true);
            }
        } catch (err) {
            console.log('fetchSystemOpen error:', err.message);
        }
    }, [fac_id, years]);

    const fetchDataApi = useCallback(async () => {
        try {
            const res = await axios.get(config.urlApi + `/scope/datasocpe/${id}`);
            const sortedActivities = res.data.map((activity) => ({
                ...activity,
                headcategories: activity.headcategories.sort((a, b) => a.id - b.id),
            }));

            setActivities(sortedActivities[0]);

            // Initialize quantities array with default values
            const initialQuantities = sortedActivities.map((activity) =>
                activity.headcategories.map((headCategory) =>
                    headCategory.data_scopes.map((data_scope) => data_scope.quantity)
                )
            );

            setQuantities(initialQuantities);
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: err.message,
            });
        }
    }, [id]);

    const onLoadDataActivity = async () => {
        try {
            const resDataCateScopes = await axios.get(`${config.urlApi}/categoryScopes/central`);
            const existingData = await axios.get(`${config.urlApi}/checkExistingData/${id}`);

            const existingCategories = [...new Set(existingData?.data.map(record => record.name))];

            const categories = resDataCateScopes?.data.filter(cateScope =>
                !existingCategories.includes(cateScope.name)
            );

            if (categories.length <= 0) return;

            Swal.fire({
                title: "Downloading ข้อมูลกิจกรรมการปล่อยก๊าซเรือนกระจก",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const res = await axios.post(`${config.urlApi}/generateActivity`, {
                activityperiod_id: id,
                fac_id: fac_id,
                campus_id: campus_id
            });

            if (res.status === 200) {
                Swal.close();
                fetchDataApi();
                Swal.fire({
                    title: 'โหลดข้อมูลเสร็จเรียบร้อยแล้ว',
                    timer: 1200,
                    timerProgressBar: true,
                });
            } else {
                throw new Error('ไม่สามารถสร้างข้อมูลกิจกรรมได้');
            }
        } catch (e) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: e.message
            });
        }
    };

    useEffect(() => {
        onLoadDataActivity();
        fetchDataApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchSystemOpen();
    }, [fetchSystemOpen]);


    const Thmonths = [
        { id: 1, month: "ม.ค" },
        { id: 2, month: "ก.พ" },
        { id: 3, month: "มี.ค" },
        { id: 4, month: "เม.ย" },
        { id: 5, month: "พ.ค" },
        { id: 6, month: "มิ.ย" },
        { id: 7, month: "ก.ค" },
        { id: 8, month: "ส.ค" },
        { id: 9, month: "ก.ย" },
        { id: 10, month: "ต.ค" },
        { id: 11, month: "พ.ย" },
        { id: 12, month: "ธ.ค" },
    ];

    const handleQuantityChange = (
        e,
        dataIndex,
        headIndex,
        activityIndex,
        quantityDataId
    ) => {
        let inputValue = e.target.value;
        const quantityDataQuantity = inputValue.toString().replace(/,/g, '');

        const newQuantities = [...quantities];
        newQuantities[activityIndex][headIndex][dataIndex] = {
            quantity: inputValue,
        };
        setQuantities(newQuantities);
        handlerSaveData(quantityDataId, quantityDataQuantity); // Pass quantityDataId and updated quantityDataQuantity to the handlerSaveData function
    };
    const handlerSaveData = async (
        quantityDataId,
        quantityDataQuantity
    ) => {
        try {
            if (
                quantityDataQuantity === "" ||
                quantityDataQuantity === null ||
                isNaN(quantityDataQuantity)
            ) {
                return; // หยุดการทำงานของฟังก์ชันทันทีหากไม่มีค่า quantityDataQuantity
            }
            const payload = {
                id: quantityDataId, // ใช้ค่า quantityDataId ใน payload
                quantity: quantityDataQuantity, // เพิ่ม quantityDataQuantity เข้าไปใน payload
            };
            await axios.put(config.urlApi + `/scope/updateQuantity/${id}`, payload);
            fetchDataApi();
        } catch (e) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: e.message,
            });
        }
    };

    const calcBODKg = (data_scopes, monthId, type) => {
        const bodName = type === 'in' ? 'BOD ขาเข้า' : 'BOD ขาออก';

        // 1. หาปริมาณน้ำเสีย (m³) ของเดือนนั้น
        const wastewater = data_scopes.find(
            (item) =>
                item.name === "ปริมาณน้ำเสีย" &&
                item.month === monthId
        )?.quantity;

        // 2. หา BOD mg/L ของเดือนนั้น
        const bodMgL = data_scopes.find(
            (item) =>
                item.name === bodName &&
                item.lci === "mg/L" &&
                item.month === monthId
        )?.quantity;

        if (!wastewater || !bodMgL) return 0;

        // สูตรคำนวณ BOD kg
        return ((parseFloat(bodMgL) / 1000) * parseFloat(wastewater));
    };

    const calcGasEmission = (data_scopes, monthId) => {
        const EF = 0.5040; // Emission factor
        const bodInKg = calcBODKg(data_scopes, monthId, 'in') || 0;
        return (bodInKg * EF) / 1000;
    };

    const getTotalGasByMonth = (monthId) => {
        if (!activities.headcategories) return 0;

        return activities.headcategories.reduce((sum, head) => {
            return sum + calcGasEmission(head.data_scopes, monthId);
        }, 0);
    };

    const getTotalGasYear = () => {
        return Thmonths.reduce((sum, Thmonth) => {
            return sum + getTotalGasByMonth(Thmonth.id);
        }, 0);
    };

    return (
        <AppShell
            page={'WaterTreatmentPlants'}
                contentPreset="form"
                title={`โรงบำบัดน้ำเสีย ${years}`}
            subtitle="บันทึกปริมาณน้ำเสีย ค่า BOD และสรุปการปล่อยก๊าซเรือนกระจกรายเดือนของระบบบำบัด"
        >
            {isDisabled && (
                <div className="alert fw-bold p-3 d-flex align-items-center" role="alert" style={{ borderColor: 'yellow', backgroundColor: '#f8f5df', borderLeft: '5px solid #ffc107' }}>
                    <WarningIcon size={40} className="me-2" /> ระบบปิดการกรอกข้อมูล
                </div>
            )}
            {activities.length === 0 ? (
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
                activities.headcategories?.map((head, headIndex) => {
                    return (
                        <div className="card" key={headIndex}>
                            <div className="card-header">
                                <h4 className="py-1">{head.head_name}</h4>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-striped table-bordered">
                                                <thead>
                                                    <tr className="text-center">
                                                        <th>รายการ</th>
                                                        <th>หน่วย</th>
                                                        {Thmonths.map((Thmonth) => (
                                                            <th key={Thmonth.id}>{Thmonth.month}</th>
                                                        ))}
                                                        <th>ปริมาณ/ปี</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {head.data_scopes.sort((a, b) => {
                                                        const namePriority = (item) => {
                                                            if (item.name === "ปริมาณน้ำเสีย") return 1;
                                                            if (item.name === "BOD ขาเข้า" || item.name === "BOD ขาออก") return 2;
                                                            return 3; // รายการอื่น
                                                        };

                                                        const priorityA = namePriority(a);
                                                        const priorityB = namePriority(b);

                                                        // 1. เรียงตาม priority
                                                        if (priorityA !== priorityB) return priorityA - priorityB;

                                                        // 2. ถ้าเป็น BOD ขาเข้า/ออก ให้ lci เดียวกันอยู่ติดกัน
                                                        if (priorityA === 2 && a.lci === b.lci) {
                                                            if (a.name === "BOD ขาเข้า" && b.name === "BOD ขาออก") return -1;
                                                            if (a.name === "BOD ขาออก" && b.name === "BOD ขาเข้า") return 1;
                                                            return 0;
                                                        }

                                                        // 3. เรียง lci Z -> A
                                                        if (a.lci < b.lci) return 1;
                                                        if (a.lci > b.lci) return -1;
                                                        return 0;
                                                    }).map((data_scope, dataIndex) => {
                                                        const isFirstRow =
                                                            dataIndex === 0 ||
                                                            head.data_scopes[dataIndex - 1].name !== data_scope.name ||
                                                            head.data_scopes[dataIndex - 1].lci !== data_scope.lci;
                                                        // Calculate total quantity per year
                                                        const filteredItems = head.data_scopes.filter(
                                                            (item) =>
                                                                item.name === data_scope.name &&
                                                                item.lci === data_scope.lci
                                                        );
                                                        const totalYearQuantity = filteredItems.reduce(
                                                            (total, item) =>
                                                                parseFloat(total) + parseFloat(item.quantity),
                                                            0
                                                        );

                                                        const filteredItems2 = head.data_scopes.filter(
                                                            (item) =>
                                                                item.name === data_scope.name &&
                                                                item.lci === 'mg/L' &&
                                                                item.quantity !== null &&
                                                                item.quantity !== undefined &&
                                                                !isNaN(parseFloat(item.quantity)) &&
                                                                parseFloat(item.quantity) !== 0
                                                        );
                                                        const averageYearQuantity = filteredItems2.length > 0
                                                            ? totalYearQuantity / filteredItems2.length
                                                            : 0;

                                                        return (
                                                            <React.Fragment key={`${data_scope.name}-${data_scope.lci}-${dataIndex}`}>
                                                                {isFirstRow && (
                                                                    <tr className="text-center">
                                                                        <td>{data_scope.name}</td>
                                                                        <td>{data_scope.lci}</td>
                                                                        {Thmonths.map((Thmonth) => {
                                                                            const quantityData = head.data_scopes.find(
                                                                                (item) =>
                                                                                    item.month === Thmonth.id &&
                                                                                    item.name === data_scope.name &&
                                                                                    item.lci === data_scope.lci
                                                                            );
                                                                            return (
                                                                                <td key={Thmonth.id}>
                                                                                    <form>
                                                                                        <NumericFormat
                                                                                            className="form-control text-end"
                                                                                            style={{
                                                                                                borderRadius: "10px",
                                                                                                border: "1px solid gray",
                                                                                                width: "90px",
                                                                                                padding: "5px",
                                                                                            }}
                                                                                            value={quantityData ? quantityData.quantity : ""}
                                                                                            thousandSeparator={true}   // ใส่ comma
                                                                                            allowNegative={false}
                                                                                            onValueChange={(values) => handleQuantityChange({ target: { value: values.value } }, dataIndex, headIndex, 0, quantityData.id)}
                                                                                            onFocus={(e) => {
                                                                                                e.target.style.backgroundColor = "#DBF1C0";
                                                                                            }}
                                                                                            disabled={isDisabled}
                                                                                        />
                                                                                    </form>
                                                                                </td>
                                                                            )
                                                                        })}

                                                                        <td>
                                                                            {
                                                                                (data_scope.name === 'BOD ขาเข้า' && data_scope.lci === 'mg/L') || (data_scope.name === 'BOD ขาออก' && data_scope.lci === 'mg/L')
                                                                                    ? Number(parseFloat(averageYearQuantity).toFixed(2)).toLocaleString()
                                                                                    : Number(parseFloat(totalYearQuantity).toFixed(2)).toLocaleString()
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        )
                                                    })}
                                                    <tr>
                                                        <td className="text-center fw-bold">BOD ขาเข้า</td>
                                                        <td className="text-center fw-bold">kg</td>
                                                        {Thmonths.map((Thmonth) => {
                                                            const result = formatNumber(calcBODKg(head.data_scopes, Thmonth.id, 'in'));
                                                            return (
                                                                <td key={Thmonth.id} className="text-center">{result}</td>
                                                            );
                                                        })}
                                                        <td className="text-center fw-bold">
                                                            {formatNumber(
                                                                Thmonths.reduce(
                                                                    (sum, Thmonth) => sum + calcBODKg(head.data_scopes, Thmonth.id, 'in'),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-center fw-bold">BOD ขาออก</td>
                                                        <td className="text-center fw-bold">kg</td>
                                                        {Thmonths.map((Thmonth) => {
                                                            const result = formatNumber(calcBODKg(head.data_scopes, Thmonth.id, 'out'));
                                                            return (
                                                                <td key={Thmonth.id} className="text-center">{result}</td>
                                                            );
                                                        })}
                                                        <td className="text-center fw-bold">
                                                            {formatNumber(
                                                                Thmonths.reduce(
                                                                    (sum, Thmonth) => sum + calcBODKg(head.data_scopes, Thmonth.id, 'out'),
                                                                    0
                                                                )
                                                            )}
                                                        </td>
                                                    </tr>
                                                    <tr
                                                        className="text-center fw-bold"
                                                        style={{ backgroundColor: '#f9f9f9' }}
                                                    >
                                                        <td>ปริมาณการปล่อยก๊าซเรือนกระจก</td>
                                                        <td>{formatChemicalFormula('tCO2')}</td>
                                                        {Thmonths.map((Thmonth) => {
                                                            const result = calcGasEmission(head.data_scopes, Thmonth.id)
                                                            return (
                                                                <td key={Thmonth.id}>{result.toFixed(2)}</td>
                                                            );
                                                        })}
                                                        <td>
                                                            {Thmonths.reduce((sum, Thmonth) => sum + calcGasEmission(head.data_scopes, Thmonth.id), 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                            </div>
                        </div>
                    )
                })
            )}
            <div className="card mb-5">
                <div className="card-header bg-secondary-subtle">
                    <h4 className="py-1 fw-semibold">สรุปการปล่อยก๊าซเรือนกระจกทั้งหมด</h4>
                </div>

                <div className="table-responsive">
                    <table className="table table-striped table-bordered">
                        <thead>
                            <tr className="text-center fw-bold">
                                <th>รายการ</th>
                                <th>หน่วย</th>
                                {Thmonths.map((Thmonth) => (
                                    <th key={Thmonth.id} >{Thmonth.month}</th>
                                ))}
                                <th>ปริมาณ/ปี</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr
                                className="text-center"
                                style={{ backgroundColor: "#f9f9f9" }}
                            >
                                <td>รวมการปล่อยก๊าซเรือนกระจกจากโรงบำบัดน้ำเสีย</td>
                                <td>{formatChemicalFormula("tCO2")}</td>

                                {Thmonths.map((Thmonth) => (
                                    <td key={Thmonth.id}>
                                        {formatNumber(getTotalGasByMonth(Thmonth.id))}
                                    </td>
                                ))}

                                <td>{formatNumber(getTotalGasYear())}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </AppShell>
    );
}

export default WaterTreatmentPlants;